import { DocumentNode, gql, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { EditorState, convertToRaw } from 'draft-js';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { AnswerForm } from '@/components/AnswerForm';
import { AnswerContent } from '@/components/Post/components/AnswerContent';
import { UseUpdateCacheOnPostCreation_PostV2Fragment } from '@/components/PostingExperience/graphql-types/UseUpdateCacheOnPostCreation_PostV2Fragment';
import { useUpdateCacheOnPostCreation } from '@/components/PostingExperience/useUpdateCacheOnPostCreation';
import { logError } from '@/imports/logging/ClientLogger';
import { answer_created, analyzeContent } from '@/analytics';

import {
  CreateAnswerModal_CreateAnswer,
  CreateAnswerModal_CreateAnswerVariables,
} from './graphql-types/CreateAnswerModal_CreateAnswer';
import { CreateAnswerModal_QuestionFragment } from './graphql-types/CreateAnswerModal_QuestionFragment';

import { CreateAnswerModalFooter } from './components/CreateAnswerModalFooter';

const QUESTION_FRAGMENT = gql`
  fragment CreateAnswerModal_QuestionFragment on QuestionV2 {
    _id
    questionTitle: title
    tags {
      _id
      name
    }
    organization {
      _id
    }
    group {
      _id
    }
  }
`;

const CREATE_ANSWER_MUTATION = gql`
  mutation CreateAnswerModal_CreateAnswer($input: CreateAnswerInput_V2!) {
    createAnswerV2(input: $input) {
      data {
        _id
        ...AnswerContent_AnswerV2Fragment
        author {
          _id
          displayName
        }
        questionPost {
          _id
          answerCount
        }
        organization {
          name
          _id
          name
          recentActivityV2(input: {}) {
            results {
              _id
            }
            hasMore
          }
        }
      }
    }
  }
  ${AnswerContent.answerFragment}
`;

type AnswerFormValue = {
  images: ({ imageId: string } | { videoId: string })[];
  body: EditorState;
  tags?: { _id: string; name: string }[];
  attachments: { versionFileId: string; fileName: string }[];
  isSponsoredPost: boolean;
  group?: { _id: string; name: string };
};

export const CreateAnswerModal: React.FC<{
  question: CreateAnswerModal_QuestionFragment;
  isOpen: boolean;
  onClose: () => void;
  defaultIsSponsoredPost?: boolean;
}> & { questionFragment: DocumentNode } = (props) => {
  const { question, onClose } = props;

  const [answerMutation, { loading }] = useMutation<
    CreateAnswerModal_CreateAnswer,
    CreateAnswerModal_CreateAnswerVariables
  >(CREATE_ANSWER_MUTATION);

  const { updateCacheOnPostCreation } = useUpdateCacheOnPostCreation();

  const createAnswer = React.useCallback(
    async (input: CreateAnswerModal_CreateAnswerVariables['input']) => {
      const result = await answerMutation({
        variables: {
          input,
        },
        update(cache, { data }) {
          if (!data?.createAnswerV2.data) {
            return;
          }
          updateCacheOnPostCreation({
            cache,
            post: data.createAnswerV2
              .data as UseUpdateCacheOnPostCreation_PostV2Fragment,
          });
        },
      });
      return result;
    },
    [answerMutation, updateCacheOnPostCreation]
  );

  const toast = useToast();

  const methods = useForm<AnswerFormValue>({
    defaultValues: {
      images: [],
      body: EditorState.createEmpty(),
      tags: question.tags || [],
      attachments: [],
      isSponsoredPost: !!props.defaultIsSponsoredPost,
      group: undefined,
    },
  });
  const { handleSubmit, formState } = methods;

  const submit = handleSubmit(async (params) => {
    try {
      const result = await createAnswer({
        data: {
          body: convertToRaw(params.body.getCurrentContent()),
          questionPostId: question._id,
          versionFileIds: params.attachments.map((attachment) => {
            return attachment.versionFileId;
          }),
          tagIds: params.tags?.map((tag) => tag._id),
          embeddedImageIds: params.images
            .filter((id): id is { imageId: string } => 'imageId' in id)
            .map((id) => id.imageId),
          ...(question.organization
            ? params.isSponsoredPost
              ? {
                sponsorData: {
                  isSponsoredPost: true,
                  audience: { groupId: params.group?._id },
                },
              }
              : {}
            : {}),
        },
        isAnnouncement: false,
      });

      // Analyze content for NLP
      const bodyText = params.body.getCurrentContent().getPlainText();
      const contentAnalysis = analyzeContent(bodyText);

      // Track answer creation with meaningful context
      if (result?.data?.createAnswerV2.data) {
        answer_created({
          entity_id: result.data.createAnswerV2.data._id,
          entity_type: 'answer',
          parent_id: question._id,
          parent_type: 'question',
          answer_question_id: question._id,
          location_posted_in: question.organization ? 'organization' : 'group',
          location_group_id: question.organization ? undefined : question.group?._id,
          location_organization_id: question.organization?._id,
          content_type: 'answer',
          post_type: 'answer',
          content_text: contentAnalysis.contentText,
          content_length: contentAnalysis.contentLength,
          content_word_count: contentAnalysis.wordCount,
          content_has_question_mark: contentAnalysis.hasQuestionMark,
          content_has_links: contentAnalysis.hasLinks,
          content_link_count: contentAnalysis.linkCount,
          content_mention_count: contentAnalysis.mentionCount,
          content_hashtag_count: contentAnalysis.hashtagCount,
          content_hashtags: contentAnalysis.hashtags,
        });
      }

      toast({
        status: 'success',
        description: 'Your answer was successfully posted.',
      });
      onClose();
    } catch (e) {
      toast({
        status: 'error',
        description: 'Failed to create answer.',
      });
      logError(e);
    }
  });

  return (
    <Modal size="xl" closeOnEsc={false} {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="center">
          <Text textStyle="compact" color="gray.600">
            ANSWERING
          </Text>
          <Text>{`'${question.questionTitle}'`}</Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody px="6" py="4">
          <FormProvider {...methods}>
            <AnswerForm
              organizationId={question.organization?._id}
              groupId={question.group?._id}
            />
          </FormProvider>
        </ModalBody>
        <CreateAnswerModalFooter>
          <Box
            mt={[2, 0]}
            display="flex"
            flexDir={['column', 'row-reverse']}
            justifyContent="space-between"
          >
            <Button
              isDisabled={formState.isSubmitted && !formState.isValid}
              onClick={submit}
              isLoading={loading}
            >
              Submit Answer
            </Button>
          </Box>
        </CreateAnswerModalFooter>
      </ModalContent>
    </Modal>
  );
};
CreateAnswerModal.questionFragment = QUESTION_FRAGMENT;
